<script lang="ts">
  import { orgLocked, orgShare } from "@nrk/origo";
  import type { Mimir } from "bildebanken-model";
  import { getCollection } from "services/collections/api";
  import { getFolderContent } from "services/folders/api";
  import { getCurrentUser, user } from "services/api";
  import type { DraggableImage } from "services/folders/types";
  import { readable, type Readable } from "svelte/store";
  import CollectionCollage from "./CollectionCollage.svelte";
  import type { CollectionTileHandler } from "./collectionTileHandler";

  export let folderId: string;
  export let name: string;
  export let ownerId: string;
  export let visibleTo: Mimir.AssetVisibility | undefined = undefined;
  export let selected: Readable<boolean> = readable(false);
  export let handler: CollectionTileHandler | undefined = undefined;

  type CollectionTileData = {
    displayName: string;
    name: string;
    images: DraggableImage[];
    collageImages: DraggableImage[];
    user: Awaited<ReturnType<typeof user>>;
  };

  const currentUser = getCurrentUser();

  const loadCollectionData = async (
    currentFolderId: string,
    currentName: string,
  ): Promise<CollectionTileData> => {
    try {
      const [ownerUser, folder, content] = await Promise.all([
        user(ownerId),
        getCollection(currentFolderId),
        getFolderContent(currentFolderId),
      ]);
      const images = content?.images ?? [];
      const collageImages = images.filter((img) => img.thumbnail).slice(0, 5);

      return {
        displayName: folder.metadata.displayName ?? folder.name,
        name: currentName,
        images,
        collageImages,
        user: ownerUser,
      };
    } catch (error) {
      throw Error("Failed to load collection tile");
    }
  };

  let collectionDataPromise: Promise<CollectionTileData>;
  $: collectionDataPromise = loadCollectionData(folderId, name);
  $: isLocked = visibleTo?.level === "owner";
  $: isShared = ownerId === currentUser.id && (visibleTo?.users?.length ?? 0) > 0;
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  role="button"
  tabindex="0"
  class="tile"
  on:click={handler?.onClickCollectionTile}
  on:dblclick={handler?.onDblClickCollectionTile}
  on:keydown
  on:mousedown={(event) => {
    if (event.shiftKey) {
      document.getSelection()?.removeAllRanges();
    }
  }}
  id={folderId}
  data-selected={$selected}
  data-testid="collection-tile"
>
  {#await collectionDataPromise}
    <CollectionCollage collageImages={[]} />

    <div class="description">
      <a href="/images?folderId={folderId}">
        <strong>{name}</strong>
      </a>
    </div>
  {:then folder}
    <CollectionCollage collageImages={folder.collageImages} />
    <div class="description">
      <a href="/images?folderId={folderId}">
        <strong>
          {folder?.displayName ?? "Ukjent tittel"}
          ({folder?.images.length ?? 0} bilder)
        </strong>
      </a>
      <div class="details-wrapper">
        {#if folder?.user?.givenName && folder?.user?.familyName}
          <div class="owner">{`${folder.user.givenName} ${folder.user.familyName}`}</div>
        {/if}
        <div>
          {#if isLocked}
            <span title="Det er kun du som kan se denne samlingen">
              {@html orgLocked}
            </span>
          {/if}
          {#if isShared}
            <span title="Denne samlingen er delt med andre">{@html orgShare}</span>
          {/if}
        </div>
      </div>
    </div>
  {:catch}
    <CollectionCollage collageImages={[]} />
    <div class="description">
      <p class="org-warning">Klarte ikke laste denne samlingen</p>
    </div>
  {/await}
</div>

<style>
  .details-wrapper {
    display: flex;
    flex-direction: row;
    gap: var(--org-xsmall);
    justify-content: space-between;
  }

  .tile {
    width: 100%;
    height: 290px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    cursor: pointer;
    border: 2px transparent solid;
    border-radius: var(--bb-standard-border-radius);
    transition: border 0.25s ease-out;
  }

  .description {
    width: 100%;
    padding: var(--org-medium);
    height: 83px;
    background: var(--org-color-shade-1);
  }

  .owner {
    font-size: 12px;
  }

  .tile:hover {
    border-color: rgba(23, 105, 255, 0.35);
    background-color: var(--bb-selected-hover-bg-color);
  }

  [data-selected="true"],
  [data-selected="true"]:hover {
    border-color: var(--org-color-primary);
  }

  [data-selected="true"] .description {
    background: var(--bb-selected-bg-color);
    color: var(--org-white);
  }
</style>
